import { Injectable, OnDestroy } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpResponse,
} from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { SpinnerOverlayService } from '../services/spinner-overlay.service';
import { ToasterService } from '@services/toaster.service';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor, OnDestroy {
    private requests: HttpRequest<any>[] = [];
    private timers = new Map<HttpRequest<any>, any>();
    private destroy = new Subject<void>();

    constructor(public loaderService: SpinnerOverlayService,
        private _toaster: ToasterService
    ) {}
    ngOnDestroy(): void {
        this.destroy.next();
        this.destroy.complete();
    }

    removeRequest(req: HttpRequest<any>) {
        const i = this.requests.indexOf(req);
        if (i >= 0) {
            this.requests.splice(i, 1);
        }
        // clear any pending timeout for this request
        const t = this.timers.get(req);
        if (t) {
            clearTimeout(t);
            this.timers.delete(req);
        }
    }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        // const subscription = this.loaderService.spinner$.subscribe();

        this.requests.push(request);
        if (this.requests.length == 1) {
            this.loaderService.show();
        }
        // hide loader after 10s if this request hasn't completed
        const timeoutId = setTimeout(() => {
            // if request still pending, remove it and hide loader if none left
            const stillPending = this.requests.indexOf(request) >= 0;
            if (stillPending) {
                this.removeRequest(request);
                if (this.requests.length === 0) {
                    this.loaderService.hide();
                }
            }
        }, 5000);
        this.timers.set(request, timeoutId);
        return new Observable((observer) => {
            const subscription = next.handle(request).pipe(takeUntil(this.destroy)).subscribe({
                next: (event) => {
                    if (event instanceof HttpResponse) {
                        this.removeRequest(request);
                        if (this.requests.length === 0) {
                            this.loaderService.hide();
                        }
                        observer.next(event.clone({ body: event?.body?.data }));
                    } else {
                        observer.next(event);
                    }
                },
                error: ({ error: { Error: response } }) => {
                    this.handleErrorToast(response);
                    this.removeRequest(request);
                    if (this.requests.length === 0) {
                        this.loaderService.hide();
                    }
                },
                complete: () => {
                    this.removeRequest(request);
                    observer.complete();
                    if (this.requests.length === 0) {
                        this.loaderService.hide();
                    }
                },
            });
            // remove request from queue when cancelled
            return () => {
                this.removeRequest(request);
                subscription.unsubscribe();
                if (this.requests.length === 0) {
                    this.loaderService.hide();
                }
            };
        });
    }

    private handleErrorToast(err: any) {
        if (!err.error) {
            this._toaster.showErrorToast('Error', err.Message);
        } else {
            this._toaster.showErrorToast('Error', 'Something went wrong');
        }
    }
}
